import { Error } from './formula.error';
import { Lex } from './formula.lexing';
import type { Descriptor, NtTree, ParseResult, Terms, Token, TweObj } from './formula.types';
import { TokenTypes } from './formula.types';

/** Lexing results grouped by left extent. */
let tweSet: Record<number, TweObj[]>;
let input: string;

/**
 * The grammar of the language.
 *
 * @remarks
 * Each member of this list has two fields:
 * 1. The `production` field is a list of objects that are either non-terminals (`{ nt: '<NT NAME HERE>' }`) or terminals (`{ t: <TOKEN TYPE HERE> }`).
 * 2. The `process` field turns the subtrees generated by the production into a tree to be passed up the chain.
 * Keep in mind that not all subtrees will necessarily exist if the user's input is not a valid formula.
 *
 * When updating this grammar, there are two constraints:
 * 1. The grammar must be left factored; i.e. Any non-terminal A cannot derive anything starting with A.
 * 2. The grammar must be follow-determined; i.e. The alternates of a non-terminal have to start with something different enough for the parser to guess which to use.
 * Keep in mind that this parser does not look ahead, so the follow-determinism constraint (#2 above) is pretty strict.
 *
 * When updating this grammar, be sure to update the `SELECT` set at the bottom of the file as appropriate.
 */
const grammar: Record<
  string,
  {
    production: ({ nt: string } | { t: Token })[];
    process: (children: (NtTree | TweObj)[]) => NtTree;
  }[]
> = {
  /* Expression ::= CompExpr
   *              | CompExpr '=' Expression */
  Expression: [
    {
      production: [{ nt: 'CompExpr' }, { nt: 'ExprEqualityTail' }],
      process: (children) =>
        ((children[1] as Terms.Group)?.values.length ?? 0) === 0
          ? (children[0] as NtTree)
          : ({
              extents: {
                start: (children[0] as NtTree).extents.start,
                end: (children[1] as Terms.Group).extents.end,
              },
              leftExpr: children[0],
              binOp: ((children[1] as Terms.Group).values[0] as TweObj).lexeme,
              rightExpr: (children[1] as Terms.Group).values[1],
            } as Terms.BinaryExpression),
    },
  ],
  ExprEqualityTail: [
    {
      production: [{ t: TokenTypes.EQUALS }, { nt: 'Expression' }],
      process: (children) =>
        ({
          extents: {
            start: (children[0] as TweObj).start,
            end: (children[1] as NtTree).extents.end,
          },
          values: children,
        }) as Terms.Group,
    },
    {
      production: [],
      process: (children) =>
        ({
          extents: { start: 0, end: 0 },
          values: children,
        }) as Terms.Group,
    },
  ],

  /* CompExpr ::= AddSubExpr
   *            | AddSubExpr /[><]=?/ CompExpr */
  CompExpr: [
    {
      production: [{ nt: 'AddSubExpr' }, { nt: 'CompExprTail' }],
      process: (children) =>
        children.length < 2 || (children[1] as Terms.Group).values.length === 0
          ? (children[0] as NtTree)
          : ({
              extents: {
                start: (children[0] as NtTree).extents.start,
                end: (children[1] as Terms.Group).extents.end,
              },
              leftExpr: children[0],
              binOp: ((children[1] as Terms.Group).values[0] as TweObj).lexeme,
              rightExpr:
                (children[1] as Terms.Group).values.length > 1
                  ? (children[1] as Terms.Group).values[1]
                  : 'MISSING',
            } as Terms.BinaryExpression),
    },
  ],
  CompExprTail: [
    {
      production: [{ t: TokenTypes.COMPOP }, { nt: 'CompExpr' }],
      process: (children) =>
        ({
          extents: {
            start: (children[0] as TweObj).start,
            end:
              children.length > 1
                ? (children[1] as NtTree).extents.end
                : (children[0] as TweObj).end,
          },

          values: children,
        }) as Terms.Group,
    },
    {
      production: [],
      process: (children) =>
        ({
          extents: { start: 0, end: 0 },
          values: children,
        }) as Terms.Group,
    },
  ],

  /* AddSubExpr ::= MulDivExpr
   *              | MulDivExpr /[+-]/ AddSubExpr */
  AddSubExpr: [
    {
      production: [{ nt: 'MulDivExpr' }, { nt: 'AddSubExprTail' }],
      process: (children) =>
        (children[1] as Terms.Group).values.length === 0
          ? (children[0] as NtTree)
          : ({
              extents: {
                start: (children[0] as NtTree).extents.start,
                end: (children[1] as Terms.Group).extents.end,
              },
              leftExpr: children[0],
              binOp: ((children[1] as Terms.Group).values[0] as TweObj).lexeme,
              rightExpr:
                (children[1] as Terms.Group).values.length > 1
                  ? (children[1] as Terms.Group).values[1]
                  : 'MISSING',
            } as Terms.BinaryExpression),
    },
  ],
  AddSubExprTail: [
    {
      production: [{ t: TokenTypes.ADDSUBOP }, { nt: 'AddSubExpr' }],
      process: (children) =>
        ({
          extents: {
            start: (children[0] as TweObj).start,
            end:
              children.length > 1
                ? (children[1] as NtTree).extents.end
                : (children[0] as TweObj).end,
          },

          values: children,
        }) as Terms.Group,
    },
    {
      production: [],
      process: (children) =>
        ({
          extents: { start: 0, end: 0 },
          values: children,
        }) as Terms.Group,
    },
  ],

  /* MulDivExpr ::= Value
   *              | Value /[*\/]|mod/i MulDivExpr */
  MulDivExpr: [
    {
      production: [{ nt: 'Value' }, { nt: 'MulDivExprTail' }],
      process: (children) =>
        children.length === 1 || (children[1] as Terms.Group).values.length === 0
          ? (children[0] as NtTree)
          : ({
              extents: {
                start: (children[0] as NtTree).extents.start,
                end: (children[1] as Terms.Group).extents.end,
              },
              leftExpr: children[0],
              binOp: ((children[1] as Terms.Group).values[0] as TweObj).lexeme,
              rightExpr:
                (children[1] as Terms.Group).values.length > 1
                  ? (children[1] as Terms.Group).values[1]
                  : 'MISSING',
            } as Terms.BinaryExpression),
    },
  ],
  MulDivExprTail: [
    {
      production: [{ t: TokenTypes.MULDIVOP }, { nt: 'MulDivExpr' }],
      process: (children) =>
        ({
          extents: {
            start: (children[0] as TweObj).start,
            end:
              children.length > 1
                ? (children[1] as NtTree).extents.end
                : (children[0] as TweObj).end,
          },

          values: children,
        }) as Terms.Group,
    },
    {
      production: [],
      process: (children) =>
        ({
          extents: { start: 0, end: 0 },
          values: children,
        }) as Terms.Group,
    },
  ],

  /* Value ::= id
   *         | Application
   *         | '(' Expression ')' */
  Value: [
    {
      production: [{ t: TokenTypes.ID }, { nt: 'Application' }],
      process: (children) =>
        children[1] && 'funcId' in children[1]
          ? ({
              extents: {
                start: (children[0] as TweObj).start,
                end: (children[1] as Terms.Application).extents.end,
              },
              funcId: (children[0] as TweObj).lexeme.trim().toUpperCase(),
              arguments: (children[1] as Terms.Application).arguments,
            } as Terms.Application) // Gotta handle this directly as the patterns overlap.
          : ({
              extents: { start: (children[0] as TweObj).start, end: (children[0] as TweObj).end },
              value: (children[0] as TweObj).lexeme.trim(),
            } as Terms.Value),
    },
    {
      production: [{ t: TokenTypes.OPEN_PARENS }, { nt: 'Expression' }, { t: TokenTypes.CLOSE_PARENS }],
      process: (children) => children[1] as NtTree,
    },
    {
      production: [],
      process: (children) => children[0] as NtTree,
    },
  ],

  /* Application ::= id '(' ArgList ')' */
  Application: [
    {
      production: [{ t: TokenTypes.OPEN_PARENS }, { nt: 'ArgList' }, { t: TokenTypes.CLOSE_PARENS }],
      process: (children) =>
        ({
          extents: {
            start: (children[0] as TweObj).start,
            end:
              (children[2] as TweObj)?.end ??
              (children[1] as NtTree)?.extents?.end ??
              (children[0] as TweObj).end,
          },

          // This gets filled in above.
          funcId: '',

          arguments: (children[1] as Terms.Group).values,
        }) as Terms.Application,
    },
    {
      production: [],
      process: (children) =>
        ({
          extents: { start: 0, end: 0 },
          values: children,
        }) as Terms.Group,
    },
  ],

  /* ArgList ::= ε
   *           | Expression
   *           | Expression ',' ArgList */
  ArgList: [
    {
      production: [{ nt: 'Expression' }, { nt: 'ArgListTail' }],
      process: (children) =>
        ({
          extents: {
            start: (children[0] as NtTree).extents.start,
            end:
              (children[1] as Terms.Group).values.length > 0
                ? (children[1] as Terms.Group).extents.end
                : (children[0] as NtTree).extents.end,
          },

          values: [children[0], ...(children[1] as Terms.Group).values],
        }) as Terms.Group,
    },
    {
      production: [],
      process: (children) =>
        ({
          extents: { start: 0, end: 0 },
          values: children,
        }) as Terms.Group,
    },
  ],
  ArgListTail: [
    {
      production: [{ t: TokenTypes.COMMA }, { nt: 'ArgList' }],
      process: (children) =>
        ({
          extents: {
            start: (children[0] as TweObj).start,
            end:
              (children[1] as Terms.Group).values.length > 0
                ? (children[1] as Terms.Group).extents.end
                : (children[0] as TweObj).end,
          },

          values: (children[1] as Terms.Group).values,
        }) as Terms.Group,
    },
    {
      production: [],
      process: (children) =>
        ({
          extents: { start: 0, end: 0 },
          values: children,
        }) as Terms.Group,
    },
  ],
};

/**
 * Parses the given input.
 *
 * @remarks
 * This is a modified LL(1) parser generator with explicit stack handling and a
 * descriptor pool à la GLL.
 */
export function Parse(rawInput: string): ParseResult {
  input = rawInput;
  tweSet = Lex(input);
  const grammarExpression = grammar['Expression']?.[0];
  if (!grammarExpression) {
    throw new EvalError('INVALID GRAMMAR! Could not locate Expression.');
  }
  let next: Descriptor = {
    position: input.length - input.trimStart().length,
    ...grammarExpression,
    parent: null,
    subtrees: [],
    errors: [],
  };

  const startTime = Date.now();

  while (next !== null) {
    const { parent, production, process, position, subtrees, errors } = next;

    // Timeout after 250 milliseconds; it shouldn't take that long.
    if (Date.now() - startTime > 250) {
      return {
        success: false,
        position,
        errors: [
          {
            message: 'Parsing timed out.',
            correction: { deleteFrom: 0, deleteTo: 0 },
          },
        ],
        tree: { extents: { start: 0, end: 0 }, value: '#TIMEOUT' } as Terms.Value,
      };
    }

    let head;

    if (production.length === 0) {
      if (parent === null) {
        // Root symbol derived, the parse is done!
        if (CheckEnd(position)) {
          return {
            success: errors.length === 0,
            tree: process(subtrees) as Terms.Value | Terms.Application | Terms.BinaryExpression,
            position,
            errors,
          };
        } else {
          return {
            success: false,
            ...Error(next, input, tweSet),
            tree: process(subtrees) as Terms.Value | Terms.Application | Terms.BinaryExpression,
          };
        }
      } else {
        // Nonterminal derived. Wrap up the trees and return to the parent.
        const { subtrees: parentSubtrees } = parent;
        next = {
          ...parent,
          parent: parent.parent,
          production: parent.production,
          subtrees: [...parentSubtrees, process(subtrees)],
          position,
          errors: [...parent.errors, ...errors],
        };
      }
    } else {
      head = production[0];
      if (!head) {
        throw new EvalError('INVALID GRAMMAR! Production head is undefined.');
      }
      if (IsNt(head)) {
        if (!(head.nt in grammar))
          throw new EvalError(`INVALID GRAMMAR! Could not locate nonterminal ${head.nt}.`);

        // Non-terminal. Find an alternate that works and go for it!
        const nextProduction = grammar[head.nt]?.find(
          (np) => np.production.length === 0 || (np.production[0] && Check(position, np.production[0])),
        );

        if (nextProduction === undefined) {
          next = Error(next, input, tweSet);
          continue;
        }

        // Set this descriptor as the parent and then try the alternate found above.
        next = {
          parent: { ...next, production: next.production.slice(1) },
          ...nextProduction,
          position,
          subtrees: [],
          errors: [],
        } as Descriptor;
      } else if (IsT(head)) {
        const token = GetToken(position, head.t);
        if (token === null || token === undefined) {
          next = Error(next, input, tweSet);
        } else {
          next = {
            ...next,
            subtrees: [...subtrees, token] as (NtTree | TweObj)[],
            production: production.slice(1),
            position: token.end,
          };
        }
      }
    }
  }

  throw new EvalError();
}

/** Checks if anything available at the current position is in the SELECT set of the provided item. */
export function Check(position: number, next: { nt: string } | { t: Token }): boolean {
  const checkSet = IsNt(next) ? SELECT[next.nt] : [next.t];
  return position in tweSet && tweSet[position]?.some((s) => checkSet?.includes(s.token)) === true;
}

/** Checks if the provided position is the end of the parseable input. */
function CheckEnd(position: number) {
  return position === input.trimEnd().length;
}

/** Checks if the expected token is available at the given position; returns `null` if not. */
function GetToken(position: number, expected: Token | null) {
  if (expected === null && position in tweSet) {
    return tweSet[position]?.[0] ?? null;
  } else if (position in tweSet && tweSet[position]?.some((i) => i.token === expected)) {
    return tweSet[position]?.find((i) => i.token == expected) ?? null;
  } else {
    return null;
  }
}

/** Guards for a non-terminal grammar item. */
export function IsNt(nt: { nt: string } | { t: Token }): nt is { nt: string } {
  return typeof nt === 'object' && 'nt' in nt;
}

/** Guards for a terminal grammar item. */
export function IsT(t: { nt: string } | { t: Token }): t is { t: Token } {
  return typeof t === 'object' && 't' in t;
}

/** The possible tokens that can be used to pick a production starting with each non-terminal. */
export const SELECT: Record<string, Token[]> = {
  Expression: [TokenTypes.ID, TokenTypes.OPEN_PARENS],
  ExprEqualityTail: [TokenTypes.EQUALS, TokenTypes.CLOSE_PARENS, TokenTypes.COMMA],
  CompExpr: [TokenTypes.ID, TokenTypes.OPEN_PARENS],
  CompExprTail: [TokenTypes.COMPOP, TokenTypes.EQUALS, TokenTypes.CLOSE_PARENS, TokenTypes.COMMA],
  AddSubExpr: [TokenTypes.ID, TokenTypes.OPEN_PARENS],
  AddSubExprTail: [TokenTypes.ADDSUBOP, TokenTypes.COMPOP, TokenTypes.EQUALS, TokenTypes.CLOSE_PARENS, TokenTypes.COMMA],
  MulDivExpr: [TokenTypes.ID, TokenTypes.OPEN_PARENS],
  MulDivExprTail: [
    TokenTypes.MULDIVOP,
    TokenTypes.ADDSUBOP,
    TokenTypes.COMPOP,
    TokenTypes.EQUALS,
    TokenTypes.CLOSE_PARENS,
    TokenTypes.COMMA,
  ],
  Value: [TokenTypes.ID, TokenTypes.OPEN_PARENS],
  Application: [TokenTypes.ID, TokenTypes.COMMA, TokenTypes.CLOSE_PARENS],
  ArgList: [TokenTypes.ID, TokenTypes.OPEN_PARENS, TokenTypes.CLOSE_PARENS],
  ArgListTail: [TokenTypes.COMMA, TokenTypes.CLOSE_PARENS],
};
